// Pick a teammate and hand them a copy of this contact — one press per
// person. Styled after the lead-tracker SharingDialog: active teammates,
// grouped, with a per-row Share button that flips to "Shared ✓".
import { useState } from "react";
import { X, UserPlus } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { shareContactWithTeammate } from "../../utils/shareContact";
import { roleLabel } from "../../utils/feedHelpers";

export default function ShareContactDialog({ contact, onClose }) {
  const [{ user, users }] = useStateValue();
  const [busyId, setBusyId] = useState("");
  const [sharedIds, setSharedIds] = useState(() => new Set());

  const teammates = (users || []).filter(
    (u) => u && u.id !== user?.id && u.type !== 1 && u.type !== 2 && !u.isDemo,
  );

  const shareTo = async (teammate) => {
    if (busyId) return;
    setBusyId(teammate.id);
    try {
      await shareContactWithTeammate({ contact, fromUser: user, toUser: teammate });
      setSharedIds((prev) => new Set(prev).add(teammate.id));
      toast.success(
        `Shared with ${teammate.firstname || teammate.name || "teammate"}`,
      );
    } catch (e) {
      console.error("Share contact failed:", e);
      toast.error(e.message || "Couldn't share this contact");
    } finally {
      setBusyId("");
    }
  };

  return (
    // z-120 keeps this above the contact drawer itself (z-110).
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 text-sm">
            Share {contact?.name || "contact"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-1.5 max-h-[55vh] overflow-y-auto">
          <p className="text-xs text-slate-500 mb-2">
            They get their own copy in My Contacts, plus a notification. Later
            edits stay separate, like a forwarded contact card.
          </p>
          {teammates.length === 0 ? (
            <p className="text-sm text-slate-400">No teammates found.</p>
          ) : (
            teammates.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between gap-3 p-2 rounded-lg border border-slate-200"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800 truncate">
                    {`${t.firstname || ""} ${t.lastname || ""}`.trim() || t.name || t.email}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {roleLabel(t.role) || ""}
                  </span>
                </span>
                <button
                  onClick={() => shareTo(t)}
                  disabled={!!busyId || sharedIds.has(t.id)}
                  className={`shrink-0 px-3 py-1 text-xs font-semibold rounded-full border ${
                    sharedIds.has(t.id)
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  } disabled:opacity-60`}
                >
                  {sharedIds.has(t.id)
                    ? "Shared ✓"
                    : busyId === t.id
                      ? "Sharing…"
                      : "Share"}
                </button>
              </div>
            ))
          )}
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
